import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const filterA5DerivativeBarriersSchema = z.object({
  structured_product_id_equals: z.coerce.number().nullable().optional(),
  structured_product_name_contains: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  barrier_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  barrier_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  payment_date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  payment_date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterA5DerivativeBarriersSchema = z.infer<
  typeof filterA5DerivativeBarriersSchema
>;

const client = "a5";

export function FilterA5DerivativeBarriers() {
  const { t } = useTranslation("tables");
  const fields: Field[] = [
    {
      name: "structured_product_name_contains",
      label: t("a5_derivative_barriers.searchbox.structured_product_name_contains", { defaultValue: "Structured product" }),
      type: "asyncAutocomplete",
      base_url: "a5/a5_static_data",
      column_name: "name",
      col_span: 2,
      filters: () => {
        const equals = { asset_class_2: "structured_product" };
        return { equals };
      },
    },
    {
      name: "barrier_date",
      type: "dateRange",
      label: t("a5_derivative_barriers.searchbox.barrier_date", { defaultValue: "Observation date" }),
      from: { name: "barrier_date_from" },
      to: { name: "barrier_date_to" },
      col_span: 2,
    },
    {
      name: "payment_date",
      type: "dateRange",
      label: t("a5_derivative_barriers.searchbox.payment_date", { defaultValue: "Payment date" }),
      from: { name: "payment_date_from" },
      to: { name: "payment_date_to" },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t("a5_derivative_barriers.searchbox.create", { defaultValue: "Creation date" }),
      from: { name: "create_from" },
      to: { name: "create_to" },
      col_span: 2,
    },
  ];
  return (
    <FilterTable
      client={client}
      schema={filterA5DerivativeBarriersSchema}
      tableName="a5_derivative_barriers"
      fields={fields}
      columns={2}
    />
  );
}
